export const realEstateFinanceCourse = {
  id: 'real-estate-finance',
  title: 'Real Estate Finance',
  description: 'Comprehensive study of real estate financing, investment analysis, mortgage markets, property valuation, and real estate development with emphasis on financial modeling and market analysis.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 3,
  category: 'Finance',
  prerequisites: ['Introduction to Finance', 'Financial Markets', 'Investment Analysis'],
  learningObjectives: [
    'Understand real estate finance principles and mortgage markets',
    'Analyze property valuation methods and investment returns',
    'Evaluate real estate development and financing strategies',
    'Apply financial modeling to real estate investments',
    'Assess real estate market risks and opportunities',
    'Examine commercial and residential financing options'
  ],
  skills: [
    'Property Valuation',
    'Real Estate Financial Modeling',
    'Mortgage Analysis',
    'Investment Property Analysis',
    'Real Estate Development Finance',
    'Market Analysis',
    'Due Diligence',
    'Risk Assessment',
    'Portfolio Management',
    'Real Estate Law Understanding'
  ],
  modules: [
    {
      id: 'module-1',
      title: 'Real Estate Finance Fundamentals',
      week: 1,
      description: 'Introduction to real estate finance, property types, and market fundamentals',
      learningObjectives: [
        'Understand the structure and participants of real estate markets',
        'Analyze different property types and their investment characteristics',
        'Apply time value of money concepts to real estate investments',
        'Evaluate real estate market cycles and economic factors'
      ],
      content: {
        type: 'lesson',
        data: {
          title: 'Real Estate Markets and Financing Principles',
          content: `# Real Estate Markets and Financing Principles

## Introduction to Real Estate Markets

### Definition and Scope
Real estate encompasses land and all permanent improvements attached to it, including buildings, structures, and natural resources. The real estate market represents the collective buying, selling, and leasing of these properties, making it one of the largest asset classes globally with a total value exceeding $280 trillion worldwide.

**Unique Characteristics of Real Estate Markets:**
- **Immobility**: Properties cannot be moved to different locations
- **Heterogeneity**: Each property is unique in location, condition, and features
- **High Transaction Costs**: Typically 5-10% of property value
- **Illiquidity**: Time-consuming buying and selling process
- **Capital Intensity**: Large financial commitments required
- **Local Market Dynamics**: Highly influenced by local economic conditions

### Real Estate as an Investment Asset Class

**Investment Characteristics:**
- **Income Generation**: Rental income and cash flow potential
- **Appreciation**: Capital gains through property value increases
- **Inflation Hedge**: Real assets typically maintain purchasing power
- **Tax Benefits**: Depreciation, interest deductions, 1031 exchanges
- **Leverage Opportunities**: Ability to use debt financing
- **Portfolio Diversification**: Low correlation with stocks and bonds

**Risk-Return Profile:**
- **Expected Returns**: Historically 8-12% annually including income and appreciation
- **Volatility**: Generally lower than stocks but higher than bonds
- **Risk Factors**: Market, credit, liquidity, interest rate, and operational risks
- **Risk Mitigation**: Diversification across property types and geographic markets

## Property Type Classification and Analysis

### Residential Real Estate

**Single-Family Homes**
- **Characteristics**: Individual dwelling units for one family
- **Investment Metrics**: Price-to-rent ratios, cash flow, appreciation potential
- **Market Drivers**: Population growth, employment, interest rates, affordability
- **Financing**: Conventional mortgages, government-backed loans, portfolio lending
- **Example**: Median home price in U.S. approximately $350,000 with 3-4% annual appreciation

**Multi-Family Properties**
- **Property Types**: Duplexes, apartments, condominiums, townhomes
- **Investment Analysis**: Cap rates (typically 4-8%), cash-on-cash returns, debt service coverage
- **Management Considerations**: Tenant relations, maintenance, regulatory compliance
- **Market Dynamics**: Supply constraints, demographic trends, urbanization
- **Case Study**: Apartment buildings in high-growth cities showing 5-7% cap rates and strong rent growth

**Specialized Residential**
- **Student Housing**: University proximity, academic calendar considerations
- **Senior Housing**: Aging population demographics, service requirements
- **Affordable Housing**: Government subsidies, tax credit programs, social impact
- **Manufactured Housing**: Lower-cost ownership option, land lease structures

### Commercial Real Estate

**Office Properties**
- **Classifications**: Class A (premium), Class B (competitive), Class C (functional)
- **Location Factors**: Central business district vs. suburban locations
- **Lease Structures**: Gross, net, triple net lease arrangements
- **Market Metrics**: Occupancy rates, rental rates per square foot, lease terms
- **Technology Impact**: Remote work trends affecting space demand and design

**Retail Properties**
- **Property Types**: Shopping centers, strip malls, standalone stores, outlet centers
- **Location Analysis**: Demographics, traffic patterns, competition, accessibility
- **Tenant Considerations**: Credit quality, lease terms, percentage rent provisions
- **E-commerce Impact**: Changing consumer behavior affecting retail real estate demand
- **Adaptive Reuse**: Converting retail spaces to mixed-use or alternative purposes

**Industrial Properties**
- **Warehouse/Distribution**: E-commerce fulfillment, logistics hubs
- **Manufacturing**: Production facilities, industrial parks
- **Specialized Industrial**: Cold storage, data centers, research facilities
- **Location Factors**: Transportation access, labor availability, utility capacity
- **Growth Drivers**: Supply chain optimization, just-in-time delivery, automation

**Hospitality Properties**
- **Hotel Types**: Full-service, limited-service, extended-stay, resort properties
- **Revenue Metrics**: Revenue per available room (RevPAR), average daily rate (ADR)
- **Management Structures**: Owner-operated, management contracts, franchise agreements
- **Market Cyclicality**: Economic sensitivity and seasonal variations
- **COVID-19 Impact**: Operational changes and recovery patterns

### Special Purpose Properties

**Healthcare Real Estate**
- **Property Types**: Hospitals, medical office buildings, skilled nursing facilities
- **Investment Characteristics**: Stable cash flows, specialized tenants, regulatory considerations
- **Demographics**: Aging population driving healthcare real estate demand
- **Triple Net Leases**: Tenant responsibility for operating expenses, taxes, insurance

**Self-Storage Facilities**
- **Market Characteristics**: Recession-resistant, high margins, scalable operations
- **Revenue Management**: Dynamic pricing, occupancy optimization
- **Development Considerations**: Zoning restrictions, market saturation analysis
- **Technology Integration**: Automated access, online rental platforms

**Data Centers**
- **Investment Drivers**: Cloud computing growth, digital transformation
- **Technical Requirements**: Power, cooling, connectivity, security
- **Lease Structures**: Long-term contracts with credit-worthy tenants
- **Geographic Considerations**: Proximity to fiber networks and power sources

## Market Participants and Their Roles

### Investors and Capital Sources

**Individual Investors**
- **Direct Ownership**: Fee simple ownership of properties
- **Investment Strategies**: Buy-and-hold, fix-and-flip, rental income focus
- **Financing Sources**: Conventional mortgages, private lending, partnerships
- **Tax Considerations**: Depreciation benefits, passive activity rules

**Institutional Investors**
- **Real Estate Investment Trusts (REITs)**: Publicly traded and private REITs
- **Pension Funds**: Long-term investment horizon, diversification objectives
- **Insurance Companies**: Stable cash flow requirements, risk management
- **Sovereign Wealth Funds**: Large-scale, strategic real estate investments

**Private Equity and Funds**
- **Core Funds**: Stable, income-producing properties (6-8% returns)
- **Value-Add Funds**: Properties requiring improvements (10-13% returns)
- **Opportunistic Funds**: Development and distressed properties (15%+ returns)
- **Fund Structures**: Limited partnerships, fee arrangements, carried interest

### Real Estate Professionals

**Real Estate Brokers and Agents**
- **Services**: Market analysis, property marketing, transaction facilitation
- **Compensation**: Commission-based (typically 3-6% of transaction value)
- **Licensing**: State-specific requirements and continuing education
- **Market Knowledge**: Local expertise, comparable sales analysis

**Property Managers**
- **Responsibilities**: Tenant relations, maintenance, financial reporting
- **Fee Structures**: Percentage of gross income (typically 3-10%)
- **Professional Standards**: Industry certifications, fiduciary duties
- **Technology Adoption**: Property management software, tenant portals

**Appraisers and Valuers**
- **Valuation Methods**: Sales comparison, income, and cost approaches
- **Licensing and Certification**: State and federal requirements
- **Professional Standards**: Uniform Standards of Professional Appraisal Practice (USPAP)
- **Market Analysis**: Highest and best use determination, market rent analysis

### Financial Institutions and Lenders

**Commercial Banks**
- **Loan Products**: Construction loans, permanent financing, bridge loans
- **Underwriting Criteria**: Debt service coverage, loan-to-value ratios, borrower experience
- **Portfolio Lending**: Loans held on balance sheet vs. sold to secondary market
- **Regulatory Requirements**: Basel III capital requirements, stress testing

**Life Insurance Companies**
- **Investment Strategy**: Long-term, stable properties matching liability duration
- **Loan Characteristics**: Fixed-rate, long-term mortgages
- **Property Preferences**: High-quality, institutional-grade properties
- **Geographic Focus**: Major metropolitan markets

**Government-Sponsored Enterprises**
- **Fannie Mae and Freddie Mac**: Multifamily lending and securitization
- **FHA Programs**: Government-backed financing for various property types
- **CMBS Market**: Commercial mortgage-backed securities issuance
- **Affordable Housing**: Low-income housing tax credits, public housing programs

## Time Value of Money in Real Estate

### Fundamental Concepts

**Present Value Analysis**
The foundation of real estate investment analysis relies on discounting future cash flows to present value using appropriate discount rates that reflect the risk and time value of money.

**Formula**: PV = CF₁/(1+r)¹ + CF₂/(1+r)² + ... + CFₙ/(1+r)ⁿ

Where:
- PV = Present Value
- CF = Cash Flow in period n
- r = Discount rate
- n = Number of periods

**Discount Rate Selection**
The discount rate should reflect:
- Risk-free rate (10-year Treasury bonds)
- Risk premium for real estate investment
- Property-specific risks (location, tenant quality, age)
- Market liquidity considerations
- Inflation expectations

### Real Estate Investment Metrics

**Net Present Value (NPV)**
NPV measures the absolute dollar amount of value created by an investment after accounting for the time value of money and investment risk.

**Decision Rule**:
- NPV > 0: Accept investment (creates value)
- NPV < 0: Reject investment (destroys value)
- NPV = 0: Indifferent (market rate of return)

**Example Calculation**:
Property purchase price: $1,000,000
Annual cash flows: Years 1-5: $80,000 each
Sale proceeds (Year 5): $1,200,000
Discount rate: 10%

NPV = -$1,000,000 + $80,000/(1.10)¹ + $80,000/(1.10)² + $80,000/(1.10)³ + $80,000/(1.10)⁴ + ($80,000 + $1,200,000)/(1.10)⁵
NPV = $98,572 (positive, accept investment)

**Internal Rate of Return (IRR)**
IRR is the discount rate that makes NPV equal to zero, representing the annualized return generated by the investment.

**Calculation**: Set NPV = 0 and solve for r
**Interpretation**: Compare IRR to required return or hurdle rate
**Limitations**: Multiple IRR solutions possible, reinvestment assumptions

**Modified Internal Rate of Return (MIRR)**
MIRR addresses IRR limitations by assuming cash flows are reinvested at a specified rate (typically the cost of capital) rather than at the IRR.

**Cash-on-Cash Return**
Simple metric measuring annual cash flow as percentage of initial equity investment.
Formula: Annual Cash Flow / Initial Equity Investment

**Example**: $50,000 annual cash flow / $200,000 equity = 25% cash-on-cash return

### Real Estate Market Cycles

**Four Phases of Real Estate Cycles**

**Phase 1: Recovery**
- Characteristics: High vacancy, low rents, construction at minimum
- Duration: Typically 1-3 years
- Investment Strategy: Opportunistic buying of distressed properties
- Indicators: Employment growth beginning, development activity minimal

**Phase 2: Expansion**
- Characteristics: Declining vacancy, rising rents, construction increasing
- Duration: Typically 3-5 years
- Investment Strategy: Value-add opportunities, moderate development
- Indicators: Job growth accelerating, construction permits increasing

**Phase 3: Hyper-Supply**
- Characteristics: Low vacancy, peak rents, construction at maximum
- Duration: Typically 1-2 years
- Investment Strategy: Disposition of properties, limited new development
- Indicators: Construction completions exceeding absorption, speculative building

**Phase 4: Recession**
- Characteristics: Rising vacancy, declining rents, construction stopping
- Duration: Typically 1-3 years
- Investment Strategy: Defensive positioning, opportunistic acquisition preparation
- Indicators: Job losses, high inventory levels, distressed sales increasing

**Economic Factors Affecting Real Estate Markets**

**Interest Rates**
- Impact on affordability and cap rates
- Federal Reserve monetary policy influence
- Relationship between bond yields and real estate returns
- Interest rate risk in floating-rate debt

**Economic Growth**
- GDP growth correlation with real estate demand
- Employment levels and income growth
- Business expansion driving commercial space demand
- Consumer confidence affecting residential purchases

**Demographics**
- Population growth and migration patterns
- Household formation rates
- Age distribution and lifecycle changes
- Immigration and urbanization trends

**Government Policy**
- Tax policy (depreciation, interest deductibility, capital gains)
- Zoning and land use regulations
- Rent control and tenant protection laws
- Environmental regulations and building codes

This comprehensive understanding of real estate markets and financing principles provides the foundation for advanced analysis of property valuation, investment strategies, and financing structures that will be covered in subsequent modules.`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Property Investment Calculator',
          description: 'Calculate NPV, IRR, and cash flows for real estate investments',
          exerciseData: {
            type: 'real_estate_analysis',
            scenario: 'Commercial Property Investment',
            parameters: {
              purchase_price: 1000000,
              rental_income: 120000,
              operating_expenses: 35000,
              growth_rate: 0.03
            }
          }
        }
      ]
    },
    {
      id: 'module-2',
      title: 'Property Valuation and Appraisal',
      week: 4,
      description: 'Valuation methods, comparable analysis, and appraisal techniques',
      content: {
        type: 'lesson',
        data: {
          title: 'Property Valuation Methods and Market Analysis',
          content: `# Property Valuation Methods and Market Analysis

## Valuation Approaches

### Sales Comparison Approach
- **Comparable Sales Analysis**: Recent similar property sales
- **Adjustment Factors**: Size, location, condition, features
- **Market Data**: Active listings and pending sales
- **Time Adjustments**: Market appreciation/depreciation

### Income Approach
- **Gross Rent Multiplier (GRM)**: Simple income valuation
- **Capitalization Rate (Cap Rate)**: NOI/Property Value
- **Discounted Cash Flow**: Multi-year income analysis
- **Terminal Value**: Property value at sale

### Cost Approach
- **Replacement Cost**: Current construction costs
- **Depreciation**: Physical, functional, economic
- **Land Value**: Comparable vacant land sales
- **Highest and Best Use**: Optimal property utilization

## Market Analysis

### Demand Factors
- **Population Growth**: Demographic trends
- **Employment**: Job market strength
- **Income Levels**: Purchasing power
- **Interest Rates**: Financing costs

### Supply Factors
- **New Construction**: Development pipeline
- **Inventory Levels**: Available properties
- **Absorption Rates**: Sales/lease velocity
- **Zoning Regulations**: Development constraints`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Property Valuation Model',
          description: 'Apply different valuation approaches to determine property value',
          exerciseData: {
            type: 'property_valuation',
            scenario: 'Multi-Family Property Appraisal',
            approaches: ['sales_comparison', 'income_approach', 'cost_approach']
          }
        }
      ]
    },
    {
      id: 'module-3',
      title: 'Mortgage Markets and Financing',
      week: 7,
      description: 'Residential and commercial mortgage products, underwriting, and secondary markets',
      content: {
        type: 'lesson',
        data: {
          title: 'Mortgage Products and Financing Structures',
          content: `# Mortgage Products and Financing Structures

## Residential Mortgages

### Loan Types
- **Conventional Loans**: Conforming and non-conforming
- **Government Loans**: FHA, VA, USDA programs
- **Adjustable Rate Mortgages (ARM)**: Variable interest rates
- **Interest-Only Loans**: Payment structure variations

### Underwriting Process
- **Borrower Analysis**: Income, credit, assets
- **Property Appraisal**: Value verification
- **Loan-to-Value (LTV)**: Risk assessment
- **Debt-to-Income (DTI)**: Payment capacity

## Commercial Mortgages

### Loan Products
- **Permanent Financing**: Long-term property loans
- **Construction Loans**: Development financing
- **Bridge Loans**: Short-term gap financing
- **Mezzanine Financing**: Hybrid debt/equity

### Underwriting Criteria
- **Debt Service Coverage Ratio (DSCR)**: Income adequacy
- **Loan-to-Value Limits**: Risk mitigation
- **Property Quality**: Location and condition
- **Sponsor Strength**: Borrower experience

## Secondary Markets

### Government Sponsored Enterprises
- **Fannie Mae**: Conventional loan purchases
- **Freddie Mac**: Mortgage market liquidity
- **Ginnie Mae**: Government loan securities

### Mortgage-Backed Securities
- **Pass-Through Securities**: Direct ownership
- **Collateralized Mortgage Obligations**: Tranched structures
- **Commercial Mortgage-Backed Securities**: Commercial property loans`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Mortgage Payment Calculator',
          description: 'Calculate mortgage payments and analyze different loan scenarios',
          exerciseData: {
            type: 'mortgage_analysis',
            scenario: 'Commercial Loan Structuring',
            variables: ['loan_amount', 'interest_rate', 'term', 'amortization']
          }
        }
      ]
    },
    {
      id: 'module-4',
      title: 'Real Estate Investment Analysis',
      week: 11,
      description: 'Investment metrics, risk analysis, and portfolio considerations',
      content: {
        type: 'lesson',
        data: {
          title: 'Investment Performance Measurement and Risk Analysis',
          content: `# Investment Performance Measurement and Risk Analysis

## Investment Metrics

### Cash Flow Analysis
- **Before-Tax Cash Flow**: Operating income minus debt service
- **After-Tax Cash Flow**: Tax considerations
- **Cash-on-Cash Return**: Annual return on equity invested
- **Equity Build-Up**: Principal reduction benefits

### Return Measures
- **Internal Rate of Return (IRR)**: Time-weighted returns
- **Net Present Value (NPV)**: Absolute value creation
- **Modified Internal Rate of Return (MIRR)**: Realistic reinvestment
- **Equity Multiple**: Total return on investment

## Risk Analysis

### Market Risks
- **Interest Rate Risk**: Financing cost changes
- **Market Value Risk**: Property appreciation/depreciation
- **Liquidity Risk**: Sale timeline uncertainty
- **Regulatory Risk**: Zoning and tax changes

### Property-Specific Risks
- **Vacancy Risk**: Tenant turnover
- **Credit Risk**: Tenant default
- **Maintenance Risk**: Unexpected repairs
- **Obsolescence Risk**: Functional deterioration

## Portfolio Considerations

### Diversification Strategies
- **Geographic Diversification**: Multiple markets
- **Property Type Mix**: Sector allocation
- **Risk-Return Profile**: Balanced portfolio
- **Correlation Analysis**: Asset relationship

### Performance Benchmarking
- **NCREIF Property Index**: Commercial property returns
- **REIT Indices**: Public market comparisons
- **Market Surveys**: Local performance data`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Investment Risk Calculator',
          description: 'Analyze investment risks and calculate risk-adjusted returns',
          exerciseData: {
            type: 'risk_analysis',
            scenario: 'Portfolio Risk Assessment',
            factors: ['market_risk', 'credit_risk', 'liquidity_risk', 'interest_rate_risk']
          }
        }
      ]
    },
    {
      id: 'module-5',
      title: 'Real Estate Development Finance',
      week: 14,
      description: 'Development financing, construction loans, and project feasibility analysis',
      content: {
        type: 'lesson',
        data: {
          title: 'Development Finance and Project Feasibility',
          content: `# Development Finance and Project Feasibility

## Development Process

### Project Phases
- **Pre-Development**: Site acquisition and planning
- **Construction**: Building phase financing
- **Lease-Up/Sale**: Stabilization period
- **Exit Strategy**: Sale or refinancing

### Feasibility Analysis
- **Market Study**: Demand and competition analysis
- **Site Analysis**: Physical and regulatory constraints
- **Financial Analysis**: Pro forma development
- **Risk Assessment**: Project-specific risks

## Construction Financing

### Loan Structure
- **Construction-to-Permanent**: Single closing process
- **Stand-Alone Construction**: Separate construction loan
- **Interest Reserve**: Carrying cost funding
- **Draw Schedule**: Progress-based funding

### Risk Management
- **Completion Guarantees**: Performance assurance
- **Cost Overrun Protection**: Budget controls
- **Lease-Up Risk**: Pre-leasing requirements
- **Market Risk**: Absorption guarantees

## Project Economics

### Development Pro Forma
- **Land Costs**: Acquisition and carrying costs
- **Construction Costs**: Hard and soft costs
- **Financing Costs**: Interest and fees
- **Developer Profit**: Return on investment

### Value Creation
- **Highest and Best Use**: Optimal development
- **Entitlement Value**: Zoning and permits
- **Construction Value**: Building improvements
- **Market Timing**: Cycle positioning`
        }
      },
      interactiveElements: [
        {
          type: 'finance_interactive',
          title: 'Development Pro Forma',
          description: 'Create comprehensive development financial analysis',
          exerciseData: {
            type: 'development_analysis',
            scenario: 'Mixed-Use Development Project',
            components: ['land_cost', 'construction_cost', 'financing_cost', 'revenue_projection']
          }
        }
      ]
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Property Valuation Analysis',
      description: 'Complete comprehensive property valuation using multiple approaches',
      week: 3,
      points: 150,
      requirements: [
        'Select real property for valuation analysis',
        'Apply sales comparison, income, and cost approaches',
        'Gather and analyze market data',
        'Reconcile values from different approaches',
        'Present findings in appraisal-style report (10-12 pages)',
        'Include market analysis and highest/best use',
        'Cite minimum 10 data sources and comparables'
      ]
    },
    {
      id: 'assignment-2',
      title: 'Mortgage Product Analysis',
      description: 'Compare and analyze different mortgage financing options',
      week: 6,
      points: 175,
      requirements: [
        'Research current mortgage products and rates',
        'Create loan comparison matrix',
        'Calculate payment scenarios and total costs',
        'Analyze borrower qualification requirements',
        'Evaluate impact of different terms and structures',
        'Recommend optimal financing strategy',
        'Present findings with supporting calculations'
      ]
    },
    {
      id: 'assignment-3',
      title: 'Investment Property Analysis',
      description: 'Comprehensive investment analysis of income-producing property',
      week: 10,
      points: 175,
      requirements: [
        'Select income-producing property for analysis',
        'Develop 10-year cash flow projections',
        'Calculate IRR, NPV, and other return metrics',
        'Perform sensitivity and scenario analysis',
        'Assess investment risks and mitigation strategies',
        'Compare to alternative investments',
        'Prepare investment recommendation memo'
      ]
    },
    {
      id: 'assignment-4',
      title: 'Development Feasibility Study',
      description: 'Complete feasibility analysis for real estate development project',
      week: 13,
      points: 200,
      requirements: [
        'Identify development opportunity and site',
        'Conduct market analysis and demand study',
        'Develop comprehensive project pro forma',
        'Analyze financing options and capital structure',
        'Assess development risks and returns',
        'Prepare executive summary for investors',
        'Present findings to development panel'
      ]
    }
  ],
  quizzes: [
    {
      id: 'quiz-1',
      title: 'Real Estate Finance Fundamentals Quiz',
      week: 2,
      points: 50,
      questions: [
        {
          type: 'calculation',
          question: 'Calculate NPV of property with $100,000 annual NOI, 7% cap rate, and 10% discount rate over 5 years',
          showWork: true
        }
      ]
    },
    {
      id: 'quiz-2',
      title: 'Property Valuation Quiz',
      week: 5,
      points: 50,
      questions: [
        {
          type: 'multiple_choice',
          question: 'Which valuation approach is most appropriate for unique properties?',
          options: ['Sales Comparison', 'Income Approach', 'Cost Approach', 'All approaches equally'],
          correct: 2
        }
      ]
    },
    {
      id: 'quiz-3',
      title: 'Mortgage Markets Quiz',
      week: 8,
      points: 50,
      questions: [
        {
          type: 'calculation',
          question: 'Calculate DSCR for property with $120,000 NOI and $100,000 annual debt service',
          correct: 1.2
        }
      ]
    },
    {
      id: 'quiz-4',
      title: 'Investment Analysis Quiz',
      week: 12,
      points: 50,
      questions: [
        {
          type: 'true_false',
          question: 'IRR assumes reinvestment at the IRR rate',
          correct: true
        }
      ]
    }
  ],
  finalExam: {
    id: 'final-exam',
    title: 'Comprehensive Real Estate Finance Examination',
    week: 16,
    points: 200,
    sections: [
      {
        name: 'Property Valuation',
        points: 50,
        topics: ['Valuation approaches', 'Market analysis', 'Appraisal process']
      },
      {
        name: 'Mortgage Finance',
        points: 50,
        topics: ['Loan products', 'Underwriting', 'Secondary markets']
      },
      {
        name: 'Investment Analysis',
        points: 50,
        topics: ['Return metrics', 'Risk analysis', 'Portfolio theory']
      },
      {
        name: 'Development Finance',
        points: 50,
        topics: ['Feasibility analysis', 'Construction loans', 'Project economics']
      }
    ]
  },
  finalProject: {
    id: 'final-project',
    title: 'Comprehensive Real Estate Investment Strategy',
    week: 15,
    points: 300,
    requirements: [
      'Develop complete real estate investment strategy',
      'Identify target market and property types',
      'Create acquisition criteria and underwriting standards',
      'Analyze financing alternatives and capital structure',
      'Develop risk management and exit strategies',
      'Present strategy to investment committee',
      'Submit comprehensive investment plan (25-30 pages)'
    ]
  },
  gradingBreakdown: {
    participation: { weight: 10, description: 'Class participation and case discussions' },
    quizzes: { weight: 20, description: 'Four module quizzes' },
    assignments: { weight: 30, description: 'Four major assignments' },
    finalExam: { weight: 20, description: 'Comprehensive final examination' },
    finalProject: { weight: 20, description: 'Investment strategy project' }
  },
  resources: [
    {
      type: 'textbook',
      title: 'Real Estate Finance and Investments',
      author: 'William Brueggeman, Jeffrey Fisher',
      edition: '16th Edition',
      isbn: '978-1259919682',
      required: true
    },
    {
      type: 'software',
      title: 'ARGUS Enterprise',
      required: false,
      purpose: 'Commercial real estate financial modeling'
    },
    {
      type: 'database',
      title: 'CoStar Property Database',
      access: 'University subscription',
      purpose: 'Market data and comparable sales'
    },
    {
      type: 'journal',
      title: 'Journal of Real Estate Finance and Economics',
      access: 'University library',
      purpose: 'Academic research and analysis'
    }
  ]
};